import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import FooterText from "../../components/footerText";
import AuthSubHeader from "src/components/authSubHeader";

import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";

import { ToastTypes } from "src/types";
import { APIEndpoints } from "src/types/apiTypes";
import { ACCEPTED_PRECERT_FILE_EXTENSIONS } from "src/constants";
import { SubmitPrecertResponse } from "./types";


const Precert: FC = (): JSX.Element => {
  const [uploadFile, changeUploadFile] = useState<FileList | null>(null);
  const [paValue, changePAValue] = useState<boolean>(true);

  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  const { showToast } = useContext(ToastContext);
  const { caseId, precertId } = useParams();





  const { loading: isFileUploading, error: submitPrecertError, data: submitPrecertData, mutate: submitPrecert } =
    useFetch<SubmitPrecertResponse>(APIEndpoints.SUBMIT_PRECERT, {}, { caseId, precertId });

  useEffect(() => {
    if (submitPrecertData || submitPrecertError) {
      if (submitPrecertData) {
        const { response } = submitPrecertData;
        showToast([response], ToastTypes.SUCCESS);
        navigate("/cases");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitPrecertData, submitPrecertError]);

  const completeMySelf = () => {
    const formData = new FormData();
    formData.append("precert[test]", "");
    if (uploadFile)
      formData.append("clinical_document", uploadFile[0]);
    formData.append("commit", "cancel");
    formData.append("precert_task", "false");
    submitPrecert(formData);
  };

  const completeOnMyBehalf = () => {
    if (uploadFile) {
      const formData = new FormData();
      formData.append("precert[test]", "");
      formData.append("clinical_document", uploadFile[0]);
      formData.append("commit", "submit_clinical_document");
      formData.append("precert_task", "false");
      submitPrecert(formData);
    }
  };





  const reset = () => {
    if (ref && ref.current) {
      ref.current.value = ""
    }
  };

  const clearDataAndForm = () => {
    changeUploadFile(null);
    reset();
  };

  const validateAndShowToast = (file: File) => {
    const caseFileExt = `.${file.name.split(".").pop()}`;
    const isExtAccepted = ACCEPTED_PRECERT_FILE_EXTENSIONS[caseFileExt];
    if (typeof isExtAccepted === "string") {
      const sizeInMBs = Number((file.size / (1024 * 1024)).toFixed(2));
      if (sizeInMBs <= 25) {
        return true;
      } else {
        showToast(["Document size should not exceed 25mb."], ToastTypes.WARNING);
      }
    } else {
      showToast(
        [`Not an acceptable format. The accepted formats are ${Object.keys(ACCEPTED_PRECERT_FILE_EXTENSIONS).toString()}`],
        ToastTypes.ERROR
      );
    }
    return false;
  };

  const handleConfirmAndExecute = (title: string, action: Function) => {
    const result = window.confirm(title);
    if (result) action();
  };

  const ConfirmAlert = (title: string, funcName: string) => {
    if (uploadFile) {
      if (validateAndShowToast(uploadFile[0])) {
        handleConfirmAndExecute(title, funcName === "completeMySelf" ? completeMySelf : completeOnMyBehalf);
      }
    } else {
      if (funcName === "completeMySelf") {
        handleConfirmAndExecute(title, completeMySelf);
      } else {
        showToast(["Document should be uploaded before submit."], ToastTypes.WARNING);
      }
    }
  };

  return (
    <div className="container-fluid d-flex flex-fill auth-page body-bg overflow-auto">
      <div className="container container-short d-flex flex-column">
        <AuthSubHeader />

        <div className="flex-fill">
          <div className="card mt-4">
            <div className="card-header bg-success-subtle">
              <h4 className="mb-0 text-black"> Upload Clinical Information </h4>
            </div>

            <div className="card-body">
              <form>
                <p>
                  My QUTENZA Connect can help by processing potential precertification or prior authorization requests on 
                  your behalf.  For assistance, fax (855.454.8746) or upload relevant supporting clinical chart 
                  documentation related to the patient’s indication (medical history, number of years since diagnosis, 
                  symptoms, medications tried, etc.).  If approved, the prior authorization will be sent to you and will 
                  be forwarded to your pre-selected specialty pharmacy (if you have chosen to use a specialty pharmacy 
                  in the QUTENZA network):
                </p>

                <div className="form-check">
                  <input className="form-check-input" type="radio" name="pa" id="paYes" checked={paValue} onChange={(e) => changePAValue(true)} />
                  <label className="form-check-label fw-bold" htmlFor="paYes">
                    Complete precertification or prior authorization on behalf of my office (fax or upload chart 
                    documentation below)
                  </label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="pa" id="paNo" checked={!paValue} onChange={(e) => changePAValue(false)} />
                  <label className="form-check-label fw-bold" htmlFor="paNo">
                    Do not complete on behalf of my office (click “skip for now” below)
                  </label>
                </div>

                <div className="card mt-2">
                  <div className="card-header bg-success-subtle">
                    <h4 className="text-black mb-0">
                      <i className="bi bi-cloud-arrow-up-fill"></i>{' '}
                      Upload Patient Chart
                    </h4>
                  </div>

                  <div className="card-body">
                    <div className="input-group mb-2">
                      <input
                        type="file" className="form-control" id="fileUpload"
                        onChange={(e) => changeUploadFile(e.target.files)} ref={ref}
                      />
                      {uploadFile ? (
                        <button
                          className="btn btn-outline-secondary" onClick={clearDataAndForm} type="button"
                          id="fileUpload" disabled={isFileUploading}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    <small className="d-block" style={{fontSize:'16px'}}>
                      Click here to upload the patient's chart. Currently we accept .txt, .pdf, .tiff, .doc, .ccd, .cda, 
                      .ccr, .ccda and image files.
                    </small>
                  </div>
                </div>

                <div className="d-flex align-items-center flex-wrap justify-content-end mt-3 gap-2">
                  <button
                    className="btn btn-blue rounded-1" type="button" name="cancel"
                    onClick={() => navigate("/cases")}
                    disabled={isFileUploading}
                  >
                    Skip For Now
                  </button>
                  <button
                    className="btn btn-success rounded-1" type="button"
                    onClick={() => ConfirmAlert("Are you sure you would like to complete on my behalf?\n This action can not be undone.", "completeOnMyBehalf")}
                    disabled={!uploadFile || isFileUploading} style={{color:'#fff', backgroundColor:'#28a745', borderColor:'#28a745'}}
                  >
                    {isFileUploading ? (<i className="spinner-border me-2" style={{
                      width: '16px',
                      height: '16px',
                      fontSize: '9px',
                    }}></i>) : null}
                    Complete On My Behalf
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* <NewPrecertComp /> */}
        </div>
        <div className="mt-3"><FooterText /></div>
      </div>
    </div>
  );
};
export default Precert;
